import { createCatalogDefaultProviderReader, deriveDefaultProviderNames, type ProviderCatalogRows } from '../compile/provider-catalog-order';
import { createLayerValueReader } from '../compile/layer-value-reader';
import { readNodeItemId } from '../compile/layer-cell-readers';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { phpJsonTruthy } from '../compile/php-json-values';
import type { ItemFieldRow } from '../compile/item-field-reader';

const MELEE_WEAPON_TYPE_ID = '1364260D-E37C-4257-AD16-5E745107811A';
const uuid = (value: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
const rowOf = (value: unknown): Record<string, unknown> | null => value !== null && typeof value === 'object' ? value as Record<string, unknown> : null;
const scalar = (value: unknown): unknown => rowOf(value) && Object.prototype.hasOwnProperty.call(value, '0') ? rowOf(value)!['0'] : value;

/** Only catalog facts: same host-first scalar/provider walk as layer_value. No inventory compile bridge.
 * null means an actual input is unavailable, never a negative answer manufactured from a thin row.
 */
export function selectThrowableWeapon(host: ItemFieldRow | null, catalog?: Readonly<Record<string, ItemFieldRow>>, metadata?: ProviderCatalogRows): boolean | null {
    if (!host) return null;
    const type = scalar(rowOf(rowOf(host)?.items)?.item_type);
    if (typeof type !== 'string' || type === '') return null;
    if (type !== 'weapon' && type !== 'natural_weapon') return false;
    if (phpJsonTruthy(rowOf(rowOf(host)?.inventory)?.enchantment_template_id)) return null;
    let names: string[];
    try { names = metadata ? deriveDefaultProviderNames(metadata) : []; } catch { return null; }
    // Rich authored references and thin {items:{item_id}} shells must resolve their actual definition.
    const source = (row: ItemFieldRow): ItemFieldRow => {
        const layers = rowOf(rowOf(row)?.item_layer_types) ?? {};
        // These source paths still require a separate construction stage. Do not guess its overrides.
        if (phpJsonTruthy(layers.enchantment_template_applied) || phpJsonTruthy(layers.lock_type)) throw new Error('throwable-source-stage-unported');
        const copied = { ...layers };
        for (const name of names) {
            const slot = layers[name];
            const entry = (value: unknown): unknown => {
                const nested = rowOf(value);
                if (nested && phpJsonTruthy(nested.template_id)) throw new Error('throwable-reference-template-stage-unported');
                if (nested && rowOf(nested.item_layer_types)) return value;
                const id = readLayerListEntryUuid(value);
                return uuid(id) ? id.toUpperCase() : value;
            };
            copied[name] = Array.isArray(slot) ? slot.map(entry) : entry(slot);
        }
        return { items: rowOf(row)?.items, item_layer_types: copied };
    };
    const provider = metadata ? createCatalogDefaultProviderReader({ catalogRows: metadata, nodeItemId: readNodeItemId,
        resolveCarrierRef: id => {
            const resolved = catalog?.[id.toUpperCase()] ?? catalog?.[id];
            if (!resolved) throw new Error(`missing-throwable-definition:${id}`);
            return source(resolved);
        } }) : null;
    const reader = createLayerValueReader({ nodeItemId: readNodeItemId,
        providers: row => {
            if (!provider) throw new Error('missing-throwable-provider-metadata');
            return provider.providers(source(row));
        } });
    let selected: ItemFieldRow;
    try { selected = source(host); } catch { return null; }
    const read = (key: string): { known: boolean; value: unknown } => {
        try { return { known: true, value: reader.read(selected, key) }; }
        catch { return { known: false, value: null }; }
    };
    const flag = read('item_throwable');
    if (flag.known && phpJsonTruthy(flag.value)) return true;
    const kind = read('weapon_type');
    if (kind.known && kind.value !== MELEE_WEAPON_TYPE_ID) return flag.known ? false : null;
    const range = read('range_end');
    if (kind.known && range.known && phpJsonTruthy(range.value)) return true;
    return flag.known && kind.known && range.known ? false : null;
}

/** Transport normalization only; SQL integer strings become the provider factory's required integer metadata. */
export function throwableProviderMetadata(rows: unknown): ProviderCatalogRows | null {
    if (!Array.isArray(rows)) return null;
    const out: Record<string, { default_provider: number | null; descent_priority: number | null }> = {};
    for (const value of rows) {
        const row = rowOf(value);
        if (!row || typeof row.item_layer_type_name !== 'string') return null;
        const integer = (value: unknown): number | null => value == null ? null : Number(value);
        out[row.item_layer_type_name] = { default_provider: integer(row.item_layer_type_default_provider),
            descent_priority: integer(row.item_layer_type_descent_priority) };
    }
    try { deriveDefaultProviderNames(out); return out; } catch { return null; }
}
